import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onUnauthorized } from './client';
import { SignInOverlay } from './auth/sign-in-overlay';
import { ME_KEY, useMe } from './auth/use-me';
import type { Me } from './auth/use-me';
import { AppHeader } from './shell/app-header';
import { Orb } from './shell/orb';
import { allowedRoute } from './shell/routes';
import { Screen } from './shell/screens';
import { ErrorState } from './shell/states';
import { TabBar } from './shell/tab-bar';
import { useRoute } from './shell/use-route';
import { failureFrom } from '@/lib/result';
import { cn } from '@/lib/utils';
import { DemoBanner } from './sources/demo-banner';
import { CreateWorkspace } from './workspaces/create-workspace';
import { NEW_WORKSPACE_EVENT } from './workspaces/workspace-form';
import { useAcceptPendingInvite } from './workspaces/use-workspaces';

/**
 * Signed in or not decides everything: the sign-in screen, the workspace
 * set-up for somebody in none yet, or the panel. Any 401 afterwards re-reads
 * `me`, so an expired session lands back on sign-in.
 */
export function App(): JSX.Element {
  const me = useMe();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  useAcceptPendingInvite(me.data !== null && me.data !== undefined);

  useEffect(
    () => onUnauthorized(() => void queryClient.invalidateQueries({ queryKey: ME_KEY })),
    [queryClient],
  );
  useEffect(() => {
    const open = (): void => setCreating(true);
    window.addEventListener(NEW_WORKSPACE_EVENT, open);
    return () => window.removeEventListener(NEW_WORKSPACE_EVENT, open);
  }, []);

  if (me.isPending) {
    return (
      <div className="grid min-h-screen place-items-center" aria-busy="true">
        <Orb size={56} />
      </div>
    );
  }
  if (me.isError) {
    const { status, detail } = failureFrom(me.error);
    return (
      <div className="mx-auto max-w-[560px] px-6 pt-[20vh]">
        <ErrorState status={status} detail={detail} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (me.data === null) return <SignInOverlay />;
  if (me.data.workspace === null) return <CreateWorkspace me={me.data} />;
  return (
    <>
      <Panel me={me.data} />
      {creating && <CreateWorkspace me={me.data} onCancel={() => setCreating(false)} />}
    </>
  );
}

function Panel({ me }: { me: Me }): JSX.Element {
  const route = allowedRoute(useRoute(), me.role);
  const chat = route.page === 'chat';
  return (
    // On Chat the page itself never scrolls: the card fills what the header (and,
    // on a phone, the tab bar) leaves, and only its columns scroll inside it.
    <div
      className={cn(
        'relative flex flex-col overflow-x-hidden',
        chat ? 'h-dvh overflow-y-hidden' : 'min-h-screen',
      )}
    >
      <div className="grid-backdrop" />
      <AppHeader me={me} active={route.page} />
      <DemoBanner />
      <main
        className={cn(
          'relative mx-auto flex w-full max-w-[1280px] flex-1 flex-col',
          // Phone paddings leave room for the fixed tab bar below.
          chat
            ? 'min-h-0 px-2.5 pt-2.5 pb-[calc(84px+env(safe-area-inset-bottom))] tab:px-7 tab:pt-6 tab:pb-9'
            : 'px-3.5 pt-5 pb-[calc(110px+env(safe-area-inset-bottom))] tab:px-7 tab:pt-9 tab:pb-20',
        )}
      >
        <Screen route={route} me={me} />
      </main>
      <TabBar role={me.role} active={route.page} />
    </div>
  );
}
